import React from 'react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';

interface SuccessStepProps {
  companyName?: string;
}

export const SuccessStep: React.FC<SuccessStepProps> = ({ companyName }) => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    // Set a flag to indicate onboarding is complete (for SSO flow)
    localStorage.setItem('onboarding_completed', 'true');
    // Redirect to login if no auth token exists — user must authenticate properly
    if (!localStorage.getItem('access_token')) {
      console.warn('No auth token found after onboarding — redirecting to login');
      navigate('/login');
      return;
    }
    navigate('/dashboard');
  };

  return (
    <Container maxWidth="md">
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          borderRadius: 3,
          background: 'linear-gradient(to bottom, #f8f9ff, #ffffff)',
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            bgcolor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <CheckCircleOutlineIcon
            color="success"
            sx={{ fontSize: 60 }}
          />
        </Box>
        
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Welcome to ASCIN{companyName ? `, ${companyName}` : ''}!
        </Typography>
        
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mb: 4 }}>
          Your supply chain intelligence platform is ready. Let's optimize your operations together.
        </Typography>
        
        <Box sx={{ 
          bgcolor: 'background.paper', 
          p: 3, 
          borderRadius: 2, 
          width: '100%',
          maxWidth: 600,
          mb: 4,
          textAlign: 'left',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            What's next?
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 0 }}>
            {[
              'Explore your dashboard to view key metrics and insights',
              'Use the AI chat to ask questions about your supply chain',
              'Run simulations to test different scenarios',
              'Monitor supplier performance and risks',
            ].map((item, index) => (
              <Box component="li" key={index} sx={{ mb: 1 }}>
                <Typography variant="body1">{item}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        
        <Button
          variant="contained"
          size="large"
          onClick={handleGetStarted}
          sx={{
            px: 6,
            py: 1.5,
            fontSize: '1.1rem',
            textTransform: 'none',
            borderRadius: 2,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 10px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.2s ease-in-out',
            }
          }}
        >
          🚀 Go to Dashboard
        </Button>
      </Paper>
    </Container>
  );
};
